import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { ArrowLeft } from "../AdminIcons";
import FormSelect from "../common/FormSelect";
import {
  useGetAllActivityLogsQuery,
  useGetAllActivityTypesQuery,
} from "../../services/activity-log/activityLogApi";
import Loader from "../common/Loader";

const dateRangeOptions = [
  { label: "Last 7 days", value: "last-7-days" },
  { label: "Last 30 days", value: "last-30-days" },
  { label: "Last 90 days", value: "last-90-days" },
];

// Convert backend action type names like "COURSE_MANAGEMENT" into "Course Management"
const formatActionTypeLabel = (name) =>
  String(name)
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// Helper function to calculate date range based on selection
const getDateRange = (range) => {
  const endDate = new Date();
  const startDate = new Date();

  if (range === "last-7-days") {
    startDate.setDate(endDate.getDate() - 7);
  } else if (range === "last-30-days") {
    startDate.setDate(endDate.getDate() - 30);
  } else if (range === "last-90-days") {
    startDate.setDate(endDate.getDate() - 90);
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

// Human readable label for the currently applied date range, e.g. "10 Aug 2026 - 17 Aug 2026"
const formatDateRangeLabel = (startDateIso, endDateIso) => {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const startLabel = formatter.format(new Date(startDateIso));
  const endLabel = "Today";

  return `${startLabel} - ${endLabel}`;
};

const ActivityLogView = () => {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedDateRange, setSelectedDateRange] = useState("last-7-days");
  const [selectedAction, setSelectedAction] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    action: "",
    dateRange: "last-7-days",
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Debounce search input before sending it to the API
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
      setCurrentPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Get date range for API query
  const dateRange = useMemo(
    () => getDateRange(appliedFilters.dateRange),
    [appliedFilters.dateRange],
  );

  // Fetch activity logs from API
  const {
    data: apiResponse,
    isLoading,
    error,
  } = useGetAllActivityLogsQuery(
    {
      page: currentPage,
      limit: rowsPerPage,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      search: debouncedSearch,
      actionType: appliedFilters.action,
    },
    {
      skip: false,
    },
  );

  const { data: activityTypesResponse } = useGetAllActivityTypesQuery();

  const actionOptions = useMemo(() => {
    const activityTypes = Array.isArray(activityTypesResponse?.data)
      ? activityTypesResponse.data
      : [];

    return [
      { label: "All Actions", value: "" },
      ...activityTypes.map((activityType) => ({
        label: formatActionTypeLabel(activityType.name),
        value: activityType.name,
      })),
    ];
  }, [activityTypesResponse]);

  // Transform API response to match component structure
  const activityRows = useMemo(() => {
    if (!apiResponse?.data) return [];
    return apiResponse.data.map((log, index) => ({
      id: log._id || `log-${index}`,
      serialNo: log.serialNo || index + 1,
      timestamp: log.createdAt
        ? new Date(log.createdAt).toLocaleString()
        : "N/A",
      // role: log.role || "N/A",
      action: log.actionType || "N/A",
      description: log.actionDescription || "N/A",
      status: log.sessionStatus || "N/A",
    }));
  }, [apiResponse]);

  const pagination = useMemo(
    () => apiResponse?.pagination || apiResponse?.data?.pagination || {},
    [apiResponse],
  );

  const totalPages = useMemo(
    () => Math.max(1, pagination.totalPages || 1),
    [pagination],
  );

  const displayedPage = pagination.currentPage || currentPage;

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const allRowsSelected =
    activityRows.length > 0 &&
    activityRows.every((row) => selectedRows.includes(row.id));

  const toggleRow = (id) => {
    setSelectedRows((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  };

  const toggleAllRows = () => {
    if (allRowsSelected) {
      setSelectedRows((current) =>
        current.filter((id) => !activityRows.some((row) => row.id === id)),
      );
      return;
    }

    setSelectedRows((current) =>
      Array.from(new Set([...current, ...activityRows.map((row) => row.id)])),
    );
  };

  return (
    <section className="min-h-screen font-['Roboto'] bg-[#F5F5F5] px-5 2xl:px-7 pb-12 pt-4 ">
      <div className="w-full max-w-[1515px] bg-white px-4 2xl:px-6 pb-8 pt-5 2xl:pt-7">
        <button type="button" aria-label="Go back" className="text-[#4A4A68]">
          <ArrowLeft />
        </button>

        <div className="mt-6 sm:mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <h2 className="text-xl sm:text-2xl  font-bold   text-[#111827]">
            Activity Log
          </h2>

          <p className="font-['Inter'] text-sm sm:text-base leading-[19px] text-[#4A4A68]">
            {formatDateRangeLabel(dateRange.startDate, dateRange.endDate)}
          </p>
        </div>

        <div className="mt-8 flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 lg:gap-10">
          <div className="relative flex-1 order-2 lg:order-1 lg:max-w-[950px]">
            <input
              type="text"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search log..."
              className="h-9 w-full rounded-full border border-[#938A96] bg-[#F1EAEA00] tracking-[6%] px-6 text-sm leading-[14px] text-[#4A4A68] placeholder:text-[#8C8CA1] focus:border-[#923D8C] focus:outline-none"
            />
            <Search
              size={20}
              className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 text-[#923D8C]"
            />
          </div>

          <div className="flex flex-1 flex-col sm:flex-row sm:items-center sm:gap-2 lg:gap-[11px] order-1 lg:order-2 gap-2">
            <FormSelect
              id="activity-log-date-range"
              options={dateRangeOptions}
              defaultValue={selectedDateRange}
              placeholder="Last 7 days"
              onChange={setSelectedDateRange}
              className="w-full sm:flex-1"
              controlWrapperClassName="mt-0"
              selectClassName="h-9 w-full rounded-full border border-[#938A96] bg-transparent px-3 text-sm text-[#4A4A68]"
              dropdownClassName="left-0 right-auto mt-1 min-w-[140px] rounded-lg border border-[#E5E7EB] bg-white py-1 shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
              optionClassName="px-3 py-2 text-sm text-[#4A4A68]"
            />

            <FormSelect
              id="activity-log-action"
              options={actionOptions}
              defaultValue={selectedAction}
              placeholder="Actions"
              onChange={setSelectedAction}
              className="w-full sm:flex-1"
              controlWrapperClassName="mt-0"
              selectClassName="h-9 w-full rounded-full border border-[#938A96] bg-transparent px-3 text-sm text-[#4A4A68]"
              dropdownClassName="left-0 right-auto mt-1 min-w-[160px] rounded-lg border border-[#E5E7EB] bg-white py-1 shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
              optionClassName="px-3 py-2 text-sm text-[#4A4A68]"
            />

            <button
              type="button"
              onClick={() => {
                setAppliedFilters({
                  action: selectedAction,
                  dateRange: selectedDateRange,
                });
                setCurrentPage(1);
              }}
              className="h-9 rounded-lg bg-[#923D8C] px-6 font-bold leading-[19px] text-white w-full sm:w-auto sm:flex-1"
            >
              Apply Filter
            </button>
          </div>
        </div>

        <div className="mt-11 bg-white rounded-lg overflow-hidden">
          {/* Responsive Table Container */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-max">
              <thead>
                <tr className="bg-[#EDE7F0] text-left font-bold leading-[19px] text-black text-xs sm:text-sm">
                  <th className="py-4 sm:py-7 pl-2 sm:pl-5 pr-1 sm:pr-3 text-center w-10 sm:w-14">
                    <input
                      type="checkbox"
                      aria-label="Select all activity logs"
                      checked={allRowsSelected}
                      onChange={toggleAllRows}
                      disabled={isLoading || activityRows.length === 0}
                      className="h-4 w-4 accent-[#BC5BAE] disabled:cursor-not-allowed"
                    />
                  </th>
                  <th className="py-4 sm:py-7 px-1 sm:px-3 hidden sm:table-cell">
                    Select all
                  </th>
                  <th className="py-4 sm:py-7 px-1 sm:px-3">S.no</th>
                  <th className="py-4 sm:py-7 px-1 sm:px-3">Time Stamp</th>
                  {/* <th className="py-4 sm:py-7 px-1 sm:px-3 hidden md:table-cell">Role</th> */}
                  <th className="py-4 sm:py-7 px-1 sm:px-3">Actions</th>
                  <th className="py-4 sm:py-7 px-1 sm:px-3 hidden lg:table-cell">
                    Description
                  </th>
                  <th className="py-4 sm:py-7 px-1 sm:px-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-2 h-24   sm:px-4 py-6 sm:py-8 text-center font-['Inter'] text-sm sm:text-xl text-[#6B7280]"
                    >
                      <div className="flex items-center justify-center">
                        <Loader size="lg" className="text-[#923D8C]" />
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-2 sm:px-4 py-6 sm:py-8 text-center font-['Inter'] text-sm sm:text-xl text-red-500"
                    >
                      Failed to load activity logs. Please try again.
                    </td>
                  </tr>
                ) : activityRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-2 sm:px-4 py-6 sm:py-8 h-24 text-center font-['Inter'] text-sm sm:text-xl text-[#6B7280]"
                    >
                      No activity logs found for selected filters.
                    </td>
                  </tr>
                ) : (
                  activityRows.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-[#EFEFFD] leading-[19px] text-black text-xs sm:text-sm hover:bg-[#F9FAFB]"
                    >
                      <td className="py-4 sm:py-6 pl-2 sm:pl-5 pr-1 sm:pr-3 text-center align-middle w-10 sm:w-14">
                        <input
                          type="checkbox"
                          aria-label={`Select row ${row.serialNo}`}
                          checked={selectedRows.includes(row.id)}
                          onChange={() => toggleRow(row.id)}
                          className="h-4 w-4 accent-[#BC5BAE]"
                        />
                      </td>
                      <td className="py-4 sm:py-6 px-1 sm:px-3 hidden sm:table-cell"></td>
                      <td className="py-4 sm:py-6 px-1 sm:px-3 whitespace-nowrap">
                        {row.serialNo}
                      </td>
                      <td className="py-4 sm:py-6 px-1 sm:px-3 whitespace-nowrap truncate">
                        {row.timestamp}
                      </td>
                      {/* <td className="py-4 sm:py-6 px-1 sm:px-3 whitespace-nowrap hidden md:table-cell">{row.role}</td> */}
                      <td className="py-4 sm:py-6 px-1 sm:px-3 whitespace-nowrap truncate">
                        {row.action}
                      </td>
                      <td className="py-4 sm:py-6 px-1 sm:px-3 whitespace-nowrap truncate hidden lg:table-cell text-[#6B7280]">
                        {row.description}
                      </td>
                      <td
                        className={`py-4 sm:py-6 capitalize px-1 sm:px-3 whitespace-nowrap font-medium ${
                          row.status.toLowerCase().includes("failed")
                            ? "text-[#D32F2F]"
                            : "text-[#1D4ED8]"
                        }`}
                      >
                        {row.status}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-5 flex items-center justify-between px-6 py-4">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1 || isLoading}
              className={`rounded-full px-6 py-1 text-sm font-medium ${
                currentPage === 1 || isLoading
                  ? "cursor-not-allowed bg-[#D9D9D9] text-[#6B7280]"
                  : "bg-[#923D8C] text-white"
              }`}
            >
              Previous
            </button>

            <p className="text-base font-medium text-black">
              Page {displayedPage}/{totalPages}
            </p>

            <button
              type="button"
              onClick={() =>
                setCurrentPage((page) => Math.min(totalPages, page + 1))
              }
              disabled={currentPage >= totalPages || isLoading}
              className={`rounded-full px-6 py-1 text-sm font-medium ${
                currentPage >= totalPages || isLoading
                  ? "cursor-not-allowed bg-[#D9D9D9] text-[#6B7280]"
                  : "bg-[#923D8C] text-white"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ActivityLogView;
